import React, { FC, useCallback, useEffect } from "react";
import {
  useGetProjectByIdQuery,
  useSetHouseNumberMutation,
} from "../../api/projectsApi";
import Button, { ButtonModeE } from "../../components/Button/Button";
import { H1 } from "../../components/Text/Text";
import { StatusE } from "../../models/enums";
import { VersionT } from "../../models/models";
import usePopup from "../../utils/hooks/usePopup";
import useShownDeliveryStatusNameFromRTKQuery from "./helpers/useShownDeliveryStatusNameFromRTKQuery";

import styles from "./setHouseNumber.module.scss";

type SetHouseNumberFooterPropTypes = {
  confirmSendToFTP: (
    asSponsorship: boolean,
    confirmed: boolean,
    houseNumberProp?: string
  ) => void;
  versionId: number;
  projectId?: number;
};

const SetHouseNumberFooter: FC<SetHouseNumberFooterPropTypes> = ({
  confirmSendToFTP,
  versionId,
  projectId,
}) => {
  const { removePopup, createPopup } = usePopup();
  const [setHouseNumber] = useSetHouseNumberMutation();
  const currentDeliveryStatusName = useShownDeliveryStatusNameFromRTKQuery();

  const { data: projectData } = useGetProjectByIdQuery(
    { id: projectId as number, detailed: true },
    { skip: !projectId }
  );

  const versionFromCache = projectData?.Versions?.find(
    (v: VersionT) => v.Id === versionId
  );

  const handleFormErrors = useCallback(
    async (e) => {
      if (e.detail.noErrors) {
        const data = e.detail.values;
        if (!data.houseNumber) {
          createPopup({
            header: <H1>Error</H1>,
            body: <p>You can't send to FTP with empty house number.</p>,
            footer: <Button onClick={() => removePopup()}>Close</Button>,
          });
          return;
        }
        const v = versionFromCache;
        const body = {
          HouseNumber: data.houseNumber,
          sponsershipHouseNumber: data.sponsershipHouseNumber,
          NotSuitableForBroadcast: v?.NotSuitableForBroadcast ?? false,
          NotSuitableForBroadcastComment:
            v?.NotSuitableForBroadcastComment ?? "",
          NotSuitableForBroadcastReason: v?.NotSuitableForBroadcastReason ?? 0,
          NotSuitableForBroadcastType: v?.NotSuitableForBroadcastType ?? 1,
          ProjectType: "VideoAd",
          useFor2Spots: data.twoSpots,
          Id: versionId,
          FileType: data.FileType?.value ?? v?.FileType,
          SecondHouseNumberType: data.sponsershipHouseNumber
            ? data.SecondHouseNumberType?.value ?? v?.SecondHouseNumberType
            : 0,
        };

        try {
          await setHouseNumber(body);
          removePopup();
          confirmSendToFTP(false, false, data.houseNumber);
        } catch (err) {
          console.log(err);
        }
      }
    },
    [
      versionFromCache,
      versionId,
      setHouseNumber,
      removePopup,
      confirmSendToFTP,
      createPopup,
    ]
  );

  useEffect(() => {
    document.addEventListener("formErrors", handleFormErrors);
    return () => {
      document.removeEventListener("formErrors", handleFormErrors);
    };
  }, [handleFormErrors]);

  const handleSendToFTP = () => {
    const triggerFields = new CustomEvent("triggerFields", {
      detail: {
        fieldNames: ["houseNumber"],
      },
    });
    const form = document.getElementById("set-house-number");
    form?.dispatchEvent(triggerFields);
  };

  return (
    <div className={styles.footerButtonsWrapper}>
      {currentDeliveryStatusName === StatusE.ApprovedAndSentToBroadcaster ||
      currentDeliveryStatusName === StatusE.DownloadedByBroadcaster ||
      currentDeliveryStatusName === StatusE.DownloadedByAllBroadcasters ? (
        <div className={styles.sendToFTPWrapper} id="sendToFTPButton">
          <Button icon="shareFTP" onClick={handleSendToFTP} useGlobalLoading>
            Send to FTP
          </Button>
        </div>
      ) : (
        <div />
      )}
      <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
        Cancel
      </Button>
      <Button form="set-house-number" useGlobalLoading>
        Save and close
      </Button>
    </div>
  );
};

export default SetHouseNumberFooter;
